import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';

type TagType =
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'p'
    | 'div'
    | 'li'
    | 'span';

interface TextProps {
    tag: TagType;
    children: React.ReactNode;
    style?: React.CSSProperties;
    className?: string;
}

const Text: React.FC<TextProps> = ({ tag, children, ...props }) => {
    const Tag = tag;
    return React.createElement(Tag, props, children);
};

interface Props {
    value: string;
    onChange: (val: string) => void;
    style?: React.CSSProperties;
    placeholder?: string;
    className?: string;
    tag?: TagType;
    multiline?: boolean;
    outlined?: boolean;
}

export default function Editable({
    value = '',
    placeholder = '',
    onChange,
    style,
    className,
    tag = 'div',
    multiline = true,
    outlined = true,
    ...rest
}: Readonly<Props>) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // this is just so we can rerun value effect if initial value is empty but then is set to something
    // if data arrive late because of client side fetching or something
    // this will make sure editable container is streched to correct height
    const [interactedWithText, setInteractedWithText] =
        useState<boolean>(false);

    useEffect(() => {
        const ref = textareaRef.current;
        const parent = ref?.parentNode as HTMLElement;
        if (ref && !interactedWithText) {
            parent.dataset.replicatedValue = formatValue(value);
        }
    }, [value]);

    const onInput = (
        e: React.FormEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
        const target = e.target as HTMLTextAreaElement | HTMLInputElement;
        const parent = target?.parentNode as HTMLElement;
        if (target && parent) {
            parent.dataset.replicatedValue = target.value;
            setInteractedWithText(true);
        }
    };

    const formatValue = (value: string) =>
        multiline ? value.replace(/<br\/>/g, '\n') : value;

    if (!onChange) {
        // read only mode
        return (
            <Text tag={tag} style={style} className={className} {...rest}>
                {value}
            </Text>
        );
    }

    return (
        <Wrapper
            className={className}
            outlined={outlined}
            multiline={multiline}
        >
            <textarea
                ref={textareaRef}
                name="editable-text"
                placeholder={placeholder}
                rows={1}
                value={formatValue(value)}
                onInput={onInput}
                onKeyDown={(e) => {
                    if (multiline === false && e.key === 'Enter') {
                        e.preventDefault();
                    }
                }}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    onChange(formatValue(e.target.value));
                }}
                style={style}
            />
        </Wrapper>
    );
}

const Wrapper = styled.div<{ outlined: boolean; multiline: boolean }>`
    /* easy way to plop the elements on top of each other and have them both sized based on the tallest one's height */
    display: grid;
    &:after {
        /* Note the weird space! Needed to preventy jumpy behavior */
        content: attr(data-replicated-value) ' ';

        /* This is how textarea text behaves */
        white-space: pre-wrap;

        /* Hidden from view, clicks, and screen readers */
        visibility: hidden;
    }
    > textarea {
        all: unset;
        /* You could leave this, but after a user resizes, then it ruins the auto sizing */
        resize: none;
        /* Firefox shows scrollbar on growth, you can hide like this. */
        overflow: hidden;

        &::placeholder {
            opacity: 0.5;
        }
    }
    > textarea,
    &:after {
        /* Identical styling required!! */
        outline: ${(props) => (props.outlined ? '1px dashed black' : 'none')};
        /* Place on top of each other */
        grid-area: 1 / 1 / 2 / 2;
        ${(props) => (props.multiline === false ? 'white-space: nowrap;' : '')};
    }
`;
